import _ from 'lodash';

import INVENTORY_TYPES from 'constants/inventoryTypes';

const PAYMENT_TYPES_WITH_PROCESSING_FEES = [
  'ADVANCE',
  'ADVANCE_DEPOSIT'
];
const FEE_AMOUNT_TYPES = {
  FLAT: 'FLAT',
  PERCENT: 'PERCENT'
};
const PAYMENT_TYPES = {
  ADVANCE: 'ADVANCE', // pay in advance - payment profile required and charged full amount
  ADVANCE_DEPOSIT: 'ADVANCE_DEPOSIT', // pay in advance - payment profile required and charged partial amount
  VENUE: 'VENUE', // pay at the venue - payment profile required and get's authorized
  NONE: 'NONE', // pay in advance - no payment profile required, charged nothing
  //
  PARTNER: 'PARTNER', // pay via a partner (no money through tablelist)
  ARRIVAL: 'ARRIVAL' // pay when you arrive at venue (money goes through tablelist)
};
const FEE_TYPES = {
  OPTIONAL: 'OPTIONAL',
  PROCESSING: 'PROCESSING'
};

function quantitySoldPercent(inventory) {
  if (!inventory) return 0;
  if (inventory.quantity === 0) return 100;

  return (inventory.quantitySold && inventory.quantity) ? 100 * (inventory.quantitySold / (inventory.quantitySold + inventory.quantity)) : 0;
}

function buyerTotal(options) {
  const quantity = 1;
  const price = options.price || 0;
  let taxPercent = options.taxPercent || 0;
  let tipPercent = options.tipPercent || 0;
  options.fees = _.isArray(options.fees) ? options.fees : [];

  let processingFeePercent = 0;

  if (PAYMENT_TYPES_WITH_PROCESSING_FEES.includes(options.paymentType)) {
    if (options.inventoryType === INVENTORY_TYPES.TICKET) {
      processingFeePercent = options.ticketProcessingFee || 0;
    } else {
      processingFeePercent = options.tableProcessingFee || 0;
    }
  }

  const calculatedFeeBreakdown = calculateFeeBreakdown(price, options.fees || [], quantity);
  let totalFees = calculatedFeeBreakdown.fees;
  let calculatedFeeTotal = calculatedFeeBreakdown.feeTotal;
  const calculatedTaxableFeeTotal = calculatedFeeBreakdown.taxableFeeTotal;

  let calculatedPrice = price * quantity;
  let depositAmount = 0;
  let dueOnArrival = 0;
  const calculatedMinimum = price * quantity;
  let taxAmount = (calculatedPrice + calculatedTaxableFeeTotal) * taxPercent;
  let tipAmount = calculatedPrice * tipPercent;

  let preProcessingTotal;

  if (options.paymentType === PAYMENT_TYPES.ADVANCE_DEPOSIT) {
    const originalPrice = calculatedPrice;
    depositAmount = options.depositAmount || 0;
    calculatedPrice = depositAmount * quantity;

    const depositDiff = price - depositAmount;

    const calculatedDepositFeeBreakdown = calculateFeeBreakdown(originalPrice, options.fees, options.quantity);
    const calculatedDepositFeeTotal = calculatedDepositFeeBreakdown.feeTotal;

    preProcessingTotal = options.depositAmount || 0;

    const depositTaxAmount = (originalPrice + calculatedTaxableFeeTotal) * taxPercent;
    const depositTipAmount = originalPrice * tipPercent;

    dueOnArrival = depositDiff + depositTaxAmount + depositTipAmount + calculatedDepositFeeTotal;

    //reset these to 0 because user doesn't pay tax + tip or fees on deposits - they are just needed for calculating the dueOnArrival amount
    taxAmount = 0;
    taxPercent = 0;
    tipAmount = 0;
    tipPercent = 0;
    totalFees = [];
    calculatedFeeTotal = 0;
  } else {
    preProcessingTotal = calculatedPrice + taxAmount + tipAmount + calculatedFeeTotal;
  }

  if (options.paymentType === PAYMENT_TYPES.VENUE || options.paymentType === PAYMENT_TYPES.NONE) {
    dueOnArrival = preProcessingTotal;
  }

  let postProcessingTotal = preProcessingTotal;

  let processingFee;
  let processingFeeTotal = 0;

  if (PAYMENT_TYPES_WITH_PROCESSING_FEES.includes(options.paymentType)) {
    processingFee = getProcessingFee(preProcessingTotal, processingFeePercent);

    if (options.userPaysProcessing) {
      totalFees.push(processingFee);

      postProcessingTotal = preProcessingTotal + processingFee.value;
      calculatedFeeTotal += processingFee.value;
    }

    processingFeeTotal = processingFee.value;
  }

  const result = {
    minimum: numberToCurrency(calculatedMinimum),
    price: numberToCurrency(calculatedPrice),
    depositAmount: numberToCurrency(depositAmount),
    subtotal: numberToCurrency(calculatedPrice),
    tax: numberToCurrency(taxAmount),
    taxPercent: numberToCurrency(taxPercent),
    tip: numberToCurrency(tipAmount),
    tipPercent: numberToCurrency(tipPercent),
    taxAndTip: numberToCurrency(taxAmount + tipAmount),
    fees: totalFees,
    feeTotal: numberToCurrency(calculatedFeeTotal),
    processingFees: processingFee ? [processingFee] : [],
    processingFeeTotal: numberToCurrency(processingFeeTotal || 0),
    processingFeePercent: processingFee ? processingFee.amount : 0,
    venueFeeTotal: calculateVenueFeeTotal(totalFees) || 0,
    venueAbsorbedProcessingFeeTotal: numberToCurrency(!options.userPaysProcessing ? processingFeeTotal : 0),
    userPaidProcessingFeeTotal: numberToCurrency(options.userPaysProcessing ? processingFeeTotal : 0),
    userPaysProcessing: options.userPaysProcessing,
    total: numberToCurrency(postProcessingTotal),
    dueOnArrival: numberToCurrency(dueOnArrival),
    paymentType: options.paymentType
  };

  return result;
}

function numberToCurrency(calculated) {
  if (isNaN(calculated) || calculated === 0) {
    return 0;
  }

  return Number(calculated.toFixed(2));
}

function calculateFeeBreakdown(price = 0, fees = [], purchaseQuantity = 1) {
  let feeTotal = 0;
  let taxableFeeTotal = 0;
  const newFees = [];

  fees.forEach(fee => {
    let feeAdd = calculateFeeAmount(price, fee);
    feeAdd *= purchaseQuantity;

    if (feeAdd) {
      feeTotal += feeAdd;

      const bookingFee = {
        name: fee.name,
        amount: fee.amount,
        type: fee.type,
        feeType: fee.feeType,
        value: feeAdd,
        taxable: fee.taxable
      };

      newFees.push(bookingFee);

      if (fee.taxable) {
        taxableFeeTotal += feeAdd;
      }
    }
  });

  return {
    fees: newFees,
    feeTotal: feeTotal,
    taxableFeeTotal: taxableFeeTotal
  };
}

function calculateFeeAmount(val, fee) {
  if (!fee || !val) return 0;
  if (!fee.amount) return 0;

  const feeAmount = fee.amount || 0;
  val = val || 0;

  switch (fee.type) {
    case FEE_AMOUNT_TYPES.FLAT:
      return numberToCurrency(feeAmount);
    case FEE_AMOUNT_TYPES.PERCENT:
      return numberToCurrency(val * feeAmount);
    default:
      return 0;
  }
}

function calculateVenueFeeTotal(fees) {
  if (!fees || fees.length === 0) return 0;

  const nonProcessingFees = _.filter(fees, {
    feeType: FEE_TYPES.OPTIONAL
  });

  const feeTotal = nonProcessingFees.reduce((sum, fee) => {
    return sum + (fee.value || 0);
  }, 0);

  return numberToCurrency(feeTotal);
}

function getProcessingFee(preProcessingTotal, processingFeePercent) {
  preProcessingTotal = preProcessingTotal || 0;

  processingFeePercent = processingFeePercent || 0;

  const feeValue = preProcessingTotal * processingFeePercent;

  return {
    name: 'Processing Fee',
    amount: processingFeePercent,
    type: FEE_AMOUNT_TYPES.PERCENT,
    value: numberToCurrency(feeValue),
    feeType: FEE_TYPES.PROCESSING
  };
}

export default {
  quantitySoldPercent,
  buyerTotal
};



// WEBPACK FOOTER //
// ./src/utils/inventoryUtils.js